<script setup>
import { computed, ref, watch } from 'vue'
import { distanceOptions } from '@/config/tripFinderOptions.js'

const props = defineProps({
  modelValue: {
    type: Number,
    default: 3,
  },
  selectedDistanceType: {
    type: String,
    default: null,
  },
  // Filters out 'day trip' when true (International Discover mode)
  isInternational: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: 'mobile',
    validator: (v) => ['mobile', 'desktop'].includes(v),
  },
})

const emit = defineEmits(['update:modelValue', 'update:selectedDistanceType'])

const filteredOptions = computed(() =>
  props.isInternational
    ? distanceOptions.filter((opt) => opt.id !== 'day')
    : distanceOptions
)

const currentConfig = computed(
  () => distanceOptions.find((opt) => opt.id === props.selectedDistanceType) || distanceOptions[0]
)

const sliderMin = ref(0)
const sliderMax = computed(() => currentConfig.value?.maxHours || 3)
const sliderStep = computed(() => currentConfig.value?.step || 0.25)

// Guard against out-of-bounds value when type changes
watch(
  () => props.selectedDistanceType,
  (newType) => {
    if (!newType) return
    const config = distanceOptions.find((opt) => opt.id === newType)
    if (config && props.modelValue > config.maxHours) {
      emit('update:modelValue', config.maxHours)
    }
  }
)

const handleTypeSelect = (id) => {
  if (props.selectedDistanceType === id) {
    emit('update:selectedDistanceType', null)
  } else {
    emit('update:selectedDistanceType', id)
    const config = distanceOptions.find((opt) => opt.id === id)
    if (config) emit('update:modelValue', config.default)
  }
}

const formatTravelTime = (hours) => {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  if (h === 0 && m === 0) return '0m'
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}hr`
  return `${h}hr ${m}m`
}

const sliderMarkers = computed(() => {
  const markers = []
  const max = sliderMax.value
  if (props.selectedDistanceType === 'day') {
    for (let i = 0; i <= max; i += 1) markers.push({ value: i, label: formatTravelTime(i) })
  } else if (props.selectedDistanceType === 'weekend') {
    for (let i = 0; i <= max; i += 2) markers.push({ value: i, label: formatTravelTime(i) })
  } else {
    for (let i = 0; i <= max; i += 6) markers.push({ value: i, label: formatTravelTime(i) })
  }
  return markers
})

const isMobile = computed(() => props.variant === 'mobile')
</script>

<template>
  <div>
    <!-- Distance Type Buttons -->
    <div :class="isMobile ? 'grid grid-cols-2 gap-3 w-full mb-8' : 'grid grid-cols-2 gap-2 mb-6'">
      <button
        v-for="opt in filteredOptions"
        :key="opt.id"
        @click="handleTypeSelect(opt.id)"
        :class="[
          isMobile
            ? 'h-[50px] w-full rounded-full text-lg font-semibold transition-all border flex items-center justify-center'
            : 'h-[44px] px-3 rounded-full text-sm font-medium transition-colors border flex items-center justify-center',
          opt.id === 'day' ? 'col-span-2' : 'col-span-1',
          isMobile && opt.id === 'day' ? 'mb-3' : '',
          selectedDistanceType === opt.id
            ? 'bg-[#4ECF84] text-white border-[#4ECF84]'
            : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
        ]"
      >
        {{ opt.label }}
      </button>
    </div>

    <!-- Slider Container -->
    <div class="px-0">
      <!-- Current Value Display -->
      <div
        :class="[
          isMobile
            ? 'flex items-center gap-2 mb-6 font-normal'
            : 'text-center mb-2 font-medium',
          selectedDistanceType ? 'text-[#2F3033]' : 'text-gray-400',
        ]"
      >
        <div :class="isMobile ? 'text-lg' : 'text-base'">Select preferred travel time</div>
        <div
          :class="[
            isMobile
              ? 'text-lg font-semibold'
              : 'inline-block mt-6 px-3 py-1 bg-white border rounded-xl font-semibold transition-all duration-300',
            selectedDistanceType
              ? isMobile ? 'text-[#4ECF84]' : 'border-[#2F3033] text-[#4ECF84]'
              : isMobile ? 'text-gray-300' : 'border-gray-200 text-gray-300',
          ]"
        >
          {{ formatTravelTime(modelValue) }}
        </div>
      </div>

      <!-- Range Input -->
      <div :class="isMobile ? 'relative w-full h-8 mb-4' : 'relative w-full h-6 mb-2'">
        <input
          type="range"
          :value="modelValue"
          :min="sliderMin"
          :max="sliderMax"
          :step="sliderStep"
          :disabled="!selectedDistanceType"
          class="custom-slider w-full appearance-none bg-transparent"
          :class="selectedDistanceType ? 'cursor-pointer' : 'cursor-not-allowed pointer-events-none opacity-60'"
          @input="emit('update:modelValue', Number($event.target.value))"
        />
      </div>

      <!-- Markers -->
      <div class="flex justify-between w-full px-1">
        <div
          v-for="marker in sliderMarkers"
          :key="marker.value"
          class="flex flex-col items-center transition-opacity duration-300"
          :class="selectedDistanceType ? 'opacity-100' : 'opacity-40'"
          :style="isMobile ? 'width: 25px' : 'width: 20px'"
        >
          <div :class="isMobile ? 'h-1.5 w-px bg-gray-300 mb-1' : 'h-1 w-px bg-gray-300 mb-1'"></div>
          <span :class="isMobile ? 'text-xs text-gray-500 whitespace-nowrap' : 'text-[10px] text-gray-500 whitespace-nowrap'">
            {{ marker.label }}
          </span>
        </div>
      </div>

      <!-- Footer note -->
      <div
        :class="[
          'text-[10px] text-center transition-opacity duration-300',
          isMobile ? 'mt-4' : 'mt-2',
          selectedDistanceType ? 'text-gray-500 opacity-100' : 'text-gray-300 opacity-40',
        ]"
      >
        Preferred travel time is one way
      </div>
    </div>
  </div>
</template>

<style scoped>
.custom-slider {
  height: 28px;
}

.custom-slider::-webkit-slider-runnable-track {
  width: 100%;
  height: 8px;
  background-color: #e5e7eb;
  border-radius: 4px;
  border: none;
}

.custom-slider::-moz-range-track {
  width: 100%;
  height: 8px;
  background-color: #e5e7eb;
  border-radius: 4px;
  border: none;
}

.custom-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 28px;
  background-color: #4ecf84;
  background-image: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.4) 0,
    rgba(255, 255, 255, 0.4) 1px,
    transparent 1px,
    transparent 3px,
    rgba(255, 255, 255, 0.4) 3px,
    rgba(255, 255, 255, 0.4) 4px,
    transparent 4px
  );
  background-repeat: no-repeat;
  background-size: 4px 12px;
  background-position: center;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #3da86b;
  margin-top: -10px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.custom-slider:active::-webkit-slider-thumb {
  transform: scale(1.15);
  box-shadow: 0 0 0 10px rgba(78, 207, 132, 0.15), 0 4px 8px rgba(0, 0, 0, 0.2);
  background-color: #45b875;
}

.custom-slider::-moz-range-thumb {
  width: 16px;
  height: 28px;
  background-color: #4ecf84;
  background-image: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.4) 0,
    rgba(255, 255, 255, 0.4) 1px,
    transparent 1px,
    transparent 3px,
    rgba(255, 255, 255, 0.4) 3px,
    rgba(255, 255, 255, 0.4) 4px,
    transparent 4px
  );
  background-repeat: no-repeat;
  background-size: 4px 12px;
  background-position: center;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #3da86b;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.custom-slider:active::-moz-range-thumb {
  transform: scale(1.15);
  box-shadow: 0 0 0 10px rgba(78, 207, 132, 0.15), 0 4px 8px rgba(0, 0, 0, 0.2);
  background-color: #45b875;
}

.custom-slider:disabled::-webkit-slider-runnable-track {
  background-color: #f3f4f6;
}

.custom-slider:disabled::-webkit-slider-thumb {
  background-color: #9ca3af !important;
  background-image: none !important;
  border-color: #9ca3af !important;
  box-shadow: none !important;
  transform: none !important;
  cursor: not-allowed;
}

.custom-slider:disabled::-moz-range-track {
  background-color: #f3f4f6;
}

.custom-slider:disabled::-moz-range-thumb {
  background-color: #9ca3af !important;
  background-image: none !important;
  border-color: #9ca3af !important;
  box-shadow: none !important;
  transform: none !important;
  cursor: not-allowed;
}
</style>
